import type { ReactNode } from 'react';
import { createElement } from 'react';
import { Avatar, ProductInfo } from '@george-labs.com/design-system';

import type { Account } from '@shared/products-types';
import { getCustomAvatarProperties } from '@shared/core-ui-avatar';
import { t } from '@luna/i18n';
import { PermittedAmount } from '@shared/core-ui-amount';
import type { ProductInfoProps, ProductWithHelpers } from '@shared/products-core';

import { getTitleAlignedWithGeorgeApp } from './titleService';

interface Options {
    dataCyPrefix?: string;
    colouredAmount?: boolean;
}

interface Product extends ProductInfoProps {
    'data-cy'?: string;
    aliasLabel?: string;
    amount?: ReactNode;
    amountLabel?: string;
    avatar?: ReactNode;
    dataCyPrefix?: string;
    productNumberLabel?: string;
    type?: string;
    typeLabel?: string;
}

export interface ProductInfoItem {
    value: string;
    product: Product;
}

export const getProductInfoItem = (
    account: ProductWithHelpers<Account>,
    { dataCyPrefix, colouredAmount = false }: Options = {},
): ProductInfoItem => {
    const avatarProperties = getCustomAvatarProperties(account.additionalData?.color);

    return {
        value: account.id,
        product: {
            color: ProductInfo.COLOR[
                account.additionalData?.color as keyof typeof ProductInfo.COLOR
            ],
            type: account.productInfoTitle,
            amount: createElement(PermittedAmount, {
                amount: account.mainAmount,
                flags: account.flags,
                coloured: colouredAmount,
            }),
            alias: getTitleAlignedWithGeorgeApp(account) ?? null,
            productNumber: account.formattedAccountNumber,
            productNumberLabel: t('app.accountNr'),
            aliasLabel: t('main.menu.v2.products.detail.aliasLabel_a11y'),
            amountLabel: t('main.menu.v2.products.detail.amountLabel_a11y'),
            avatar: createElement(Avatar, {
                icon: account.icon,
                imageUrl: account.imageData || account.alternativeImageUrl,
                color: avatarProperties.color,
                backgroundColor: avatarProperties.backgroundColor,
                variant: avatarProperties.variant,
                ...(account?.additionalData?.isFavorite && {
                    badgeIcon: Avatar.BADGE_ICON.STAR_FILLED,
                    badgeColor: Avatar.BADGE_COLOR.HONEY,
                }),
            }),
            typeLabel: t('main.menu.v2.products.detail.typeLabel_a11y'),
            ...(dataCyPrefix && { 'data-cy': `${dataCyPrefix}${account.id}` }),
        },
    };
};

export const getProductInfoItemsFromAccounts = (
    accounts: ProductWithHelpers<Account>[],
    options?: Options,
): ProductInfoItem[] => accounts.map((account) => getProductInfoItem(account, options));
